import React, { useState, useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './store';
import AppRoutes from './routes/AppRoutes';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Cookies from 'js-cookie';
import { loginSuccess } from './features/auth/authSlice';
import { ErrorProvider, useError } from './contexts/ErrorContext';
import ErrorMessagePopup from './CommanService/ErrorMessagePopup';

const cssLoader = (
  <div style={{
    position: 'fixed',   
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(255,255,255,0.8)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px'
  }}>
    <div className="bouncing-loader">
      <div></div><div></div><div></div>
    </div>
    <p style={{ 
      color: '#333', 
      fontSize: '18px', 
      fontWeight: '600',
      margin: 0,
      textAlign: 'center',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
    }}>
      Data is loading, please wait...
    </p>
    <style>
      {`
      .bouncing-loader {
        display: flex;
        align-items: flex-end;
        justify-content: center;
        height: 60px;
        gap: 10px;
      }
      .bouncing-loader div {
        width: 18px;
        height: 18px;
        background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
        border-radius: 50%;
        animation: bouncing-loader 0.6s infinite alternate;
        box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
      }
      .bouncing-loader div:nth-child(2) {
        animation-delay: 0.2s;
      }
      .bouncing-loader div:nth-child(3) {
        animation-delay: 0.4s;
      }
      @keyframes bouncing-loader {
        to {
          transform: translateY(-30px) scale(1.1);
          box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
        }
      }
      `}
    </style>
  </div>
);

function AppContent() {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const { isAuthenticated, loading: authLoading } = useSelector(state => state.auth);
  const { errorMessage, errorSubMessage, showError, hideErrorMessage, showErrorMessage } = useError();

  useEffect(() => {
    const initializeApp = async () => {
      // Restore session from cookies/sessionStorage
      const token = Cookies.get('Token');
      const user = sessionStorage.getItem('user');
      if (token && user) {
        try {
          dispatch(loginSuccess({ data: JSON.parse(user), password: token }));
        } catch (e) {
          // If user data is corrupted, clear session
          sessionStorage.removeItem('user');
          Cookies.remove('Token');
        }
      }
      // Simulate initial load
      const timer = setTimeout(() => setLoading(false), 1200);
      return () => clearTimeout(timer);
    };

    initializeApp();
  }, [dispatch]);

  // Listen for API error events
  useEffect(() => {
    const handleApiError = (event) => {
      showErrorMessage(event.detail.message, event.detail.subMessage);
    };

    const handleHideApiError = () => {
      hideErrorMessage();
    };

    window.addEventListener('showApiError', handleApiError);
    window.addEventListener('hideApiError', handleHideApiError);
    
    return () => {
      window.removeEventListener('showApiError', handleApiError);
      window.removeEventListener('hideApiError', handleHideApiError);
    };
  }, [showErrorMessage, hideErrorMessage]);

  // Show loader while validating token or during initial load
  if (loading || authLoading) {
    return cssLoader;
  }

  return (
    <div className="App">
      <AppRoutes />
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={true} toastClassName="custom-toast" bodyClassName="custom-toast-body" closeButton={false} />
      <ErrorMessagePopup
        message={errorMessage}
        subMessage={errorSubMessage}
        isVisible={showError}
        onClose={hideErrorMessage}
      />
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ErrorProvider>
        <AppContent />
      </ErrorProvider>
    </Provider>
  );
}

export default App;
